/**
 * `clawnify agents` — the FDE's agent-management verbs.
 *
 *   clawnify agents [list]          list the agents on the org's server
 *   clawnify agents pull [dir]      fetch an agent's AGENTS.md + skills/ + flows/
 *                                   to edit locally
 *   clawnify agents push [dir]      push the edited files back (additive; the
 *                                   agent picks them up next session, no restart)
 *
 * Backed by GET /servers/:id/agents, GET /servers/:id/agent-config, and
 * POST /servers/:id/deploy-agent. See docs/internal/fde-strategy.md.
 */

import fs from "node:fs";
import path from "node:path";
import { api } from "../lib/api.js";
import { resolveServerId } from "../lib/servers.js";
import { readProject, saveProject } from "../lib/config.js";

interface AgentRow {
  id: string;
  name?: string;
  status?: string;
  sessionCount?: number;
}

interface AgentFile {
  path: string;
  content: string;
}

interface CommonOpts {
  server?: string;
  agent?: string;
}

export async function agentsListCommand(opts: { server?: string }): Promise<void> {
  const serverId = await resolveServerId(opts.server);
  const res = await api.get(`/servers/${serverId}/agents`);
  if (!res.ok) {
    console.error(`Failed to list agents (${res.status}).`);
    process.exit(1);
  }
  const payload = (await res.json()) as { agents: AgentRow[] } | AgentRow[];
  const agents = Array.isArray(payload) ? payload : payload.agents;
  if (!agents || agents.length === 0) {
    console.log("No agents on this server.");
    return;
  }

  const idW = Math.max(2, ...agents.map((a) => (a.id ?? "").length));
  const nameW = Math.max(4, ...agents.map((a) => (a.name ?? "").length));
  console.log(`${"ID".padEnd(idW)}  ${"Name".padEnd(nameW)}  ${"Status".padEnd(8)}  Sessions`);
  console.log(`${"─".repeat(idW)}  ${"─".repeat(nameW)}  ${"─".repeat(8)}  ${"─".repeat(8)}`);
  for (const a of agents) {
    console.log(
      `${(a.id ?? "").padEnd(idW)}  ${(a.name ?? "").padEnd(nameW)}  ${(a.status ?? "").padEnd(8)}  ${a.sessionCount ?? 0}`,
    );
  }
}

export async function agentsPullCommand(dir: string | undefined, opts: CommonOpts): Promise<void> {
  const serverId = await resolveServerId(opts.server);
  const agent = opts.agent || "main";

  const res = await api.get(`/servers/${serverId}/agent-config?agent=${encodeURIComponent(agent)}`);
  if (!res.ok) {
    if (res.status === 404) console.error(`Agent "${agent}" not found on this server.`);
    else console.error(`Failed to read agent config (${res.status}).`);
    process.exit(1);
  }
  const { files } = (await res.json()) as { files: AgentFile[] };

  const target = dir ? path.resolve(dir) : process.cwd();
  fs.mkdirSync(target, { recursive: true });
  let written = 0;
  for (const f of files ?? []) {
    // The API only returns AGENTS.md / skills/** / flows/*.json, but guard the
    // returned path against traversal before writing to disk.
    const norm = path.posix.normalize(f.path);
    if (norm.startsWith("/") || norm.split("/").includes("..")) continue;
    const abs = path.join(target, norm);
    fs.mkdirSync(path.dirname(abs), { recursive: true });
    fs.writeFileSync(abs, f.content);
    written++;
  }

  // Remember server + agent so `clawnify agents push` targets the same place.
  const existing = readProject(target) ?? {};
  saveProject(target, { ...existing, kind: "agent", server_id: serverId, agent_name: agent });

  console.log(`Pulled ${written} file(s) for agent "${agent}" → ${target}`);
  console.log(`Edit AGENTS.md / skills / flows, then push:  clawnify agents push${dir ? ` ${dir}` : ""}`);
}

export async function agentsPushCommand(dir: string | undefined, opts: CommonOpts): Promise<void> {
  const target = dir ? path.resolve(dir) : process.cwd();
  const linked = readProject(target);
  const serverId = await resolveServerId(opts.server || linked?.server_id);
  const agent = opts.agent || linked?.agent_name || "main";

  const files: AgentFile[] = [];
  const add = (rel: string) => {
    try {
      files.push({ path: rel, content: fs.readFileSync(path.join(target, rel), "utf8") });
    } catch {
      /* file absent — skip */
    }
  };
  add("AGENTS.md");
  const walk = (relDir: string) => {
    let names: string[];
    try {
      names = fs.readdirSync(path.join(target, relDir));
    } catch {
      return;
    }
    for (const name of names) {
      const rel = `${relDir}/${name}`;
      let isDir = false;
      try {
        isDir = fs.statSync(path.join(target, rel)).isDirectory();
      } catch {
        continue;
      }
      if (isDir) walk(rel);
      else add(rel);
    }
  };
  walk("skills");
  let flowNames: string[] = [];
  try {
    flowNames = fs.readdirSync(path.join(target, "flows"));
  } catch {
    /* no flows dir */
  }
  for (const name of flowNames) if (name.endsWith(".json")) add(`flows/${name}`);

  if (files.length === 0) {
    console.error(`No agent files (AGENTS.md / skills / flows) found in ${target}. Run \`clawnify agents pull\` first.`);
    process.exit(1);
  }

  const res = await api.post(`/servers/${serverId}/deploy-agent`, { agent, files });
  if (!res.ok) {
    console.error(`Failed to push (${res.status}).`);
    process.exit(1);
  }
  console.log(
    `Pushed ${files.length} file(s) to agent "${agent}". Additive — the agent picks them up on its next session (no restart).`,
  );
}

export async function agentsSkillsCommand(
  agent: string | undefined,
  opts: { server?: string },
): Promise<void> {
  const serverId = await resolveServerId(opts.server);
  const who = agent || "main";

  const res = await api.get(`/servers/${serverId}/agent-skills?agent=${encodeURIComponent(who)}`);
  if (!res.ok) {
    if (res.status === 404) console.error(`Agent "${who}" not found on this server.`);
    else console.error(`Failed to read skills (${res.status}).`);
    process.exit(1);
  }
  const data = (await res.json()) as { agent: string; skills: unknown };

  // The EFFECTIVE inventory (workspace + shared managed-root + bundled, with
  // eligibility) as OpenClaw's loader reports it — richer than what's in the
  // agent's own workspace. Printed as JSON (the shape is OpenClaw's): a coding
  // agent parses it, a human can `| jq`.
  console.log(JSON.stringify(data.skills, null, 2));
}

export async function agentsGrantSkillCommand(
  skill: string,
  opts: { server?: string; to?: string; all?: boolean },
): Promise<void> {
  const serverId = await resolveServerId(opts.server);
  if (!opts.all && !opts.to) {
    console.error(
      "Specify who gets the skill: --to <agent> (one specialist) or --all (every agent on the box).",
    );
    process.exit(1);
  }
  const scope = opts.all ? "tree" : "agent";
  const payload: Record<string, unknown> = { skill, action: "grant", scope };
  if (scope === "agent") payload.slug = opts.to;

  const res = await api.post(`/servers/${serverId}/agent-skills`, payload);
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    // Grant symlinks an EXISTING main-workspace skill; a 404 means it isn't
    // authored yet, so point the FDE at the author step instead of a bare error.
    if (res.status === 404 && /source skill not found/i.test(data.error ?? "")) {
      console.error(
        `Skill "${skill}" isn't in the main agent's workspace, so there's nothing to grant yet.\n` +
          `Author it first:  clawnify agents pull ./main --agent main  →  add skills/${skill}/SKILL.md  →  clawnify agents push ./main`,
      );
    } else {
      console.error(`Failed to grant skill (${res.status})${data.error ? `: ${data.error}` : ""}.`);
    }
    process.exit(1);
  }

  const who = opts.all ? "every agent (shared managed root; includes future agents)" : `agent "${opts.to}"`;
  console.log(`Granted skill "${skill}" to ${who}. Picked up on the agent's next session (no restart). Reversible.`);
}

export async function agentsCreateCommand(
  slug: string,
  opts: { server?: string; name?: string; description?: string; yes?: boolean },
): Promise<void> {
  const serverId = await resolveServerId(opts.server);

  // Creating an agent runs several openclaw calls and a synchronous gateway
  // restart (~20-40s) that briefly interrupts every agent on the box. Gate it
  // behind --yes so an autonomous FDE has to consciously confirm the disruption.
  if (!opts.yes) {
    console.error(
      `This creates agent "${slug}" and RESTARTS the gateway (~20-40s), briefly interrupting every agent on the server.\n` +
        `Re-run with --yes once the user has confirmed:  clawnify agents create ${slug} --yes`,
    );
    process.exit(1);
  }

  const payload: Record<string, unknown> = { slug };
  if (opts.name) payload.name = opts.name;
  if (opts.description) payload.description = opts.description;

  const res = await api.post(`/servers/${serverId}/agents`, payload);
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    console.error(`Failed to create agent (${res.status})${data.error ? `: ${data.error}` : ""}.`);
    process.exit(1);
  }
  console.log(
    `Created agent "${slug}". The gateway was restarted and it onboards shortly. ` +
      `Author it with:  clawnify agents pull ./${slug} --agent ${slug}`,
  );
}
